import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronLeft, House, ReceiptText, SquarePlus } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { APP_NAME, sectionBar } from "@/lib/nav";
import { useNotifications } from "@/lib/notifications";
import { cn } from "@/lib/utils";
import { AppMenu } from "@/components/app-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { UserMenu } from "@/components/user-menu";
import { Button } from "@/components/ui/button";

/**
 * The phone frame: a single centred max-w-lg column filling the small viewport height.
 * Fixed app bar on top, fixed bottom tab bar, only the content scrolls.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const bar = sectionBar(pathname);

  return (
    <div className="mx-auto flex h-svh w-full max-w-lg flex-col border-x bg-background">
      <header className="pt-safe sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="flex h-14 items-center gap-1 px-2">
          {bar ? (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Back"
                onClick={() => navigate(bar.back)}
              >
                <ChevronLeft className="size-6" />
              </Button>
              <h1 className="min-w-0 truncate text-base font-semibold">{bar.title}</h1>
            </>
          ) : (
            <>
              <AppMenu />
              <h1 className="min-w-0 truncate pl-1 text-base font-semibold">{APP_NAME}</h1>
            </>
          )}
          <div className="ml-auto flex items-center gap-1">
            <BellButton />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>

      <BottomNav />
    </div>
  );
}

function UnreadBadge({ className }: { className?: string }) {
  const { unreadCount } = useNotifications();
  if (unreadCount <= 0) return null;
  return (
    <span
      className={cn(
        "absolute flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white",
        className
      )}
    >
      {unreadCount > 99 ? "99+" : unreadCount}
    </span>
  );
}

function BellButton() {
  const { unreadCount } = useNotifications();
  return (
    <Button
      nativeButton={false}
      variant="ghost"
      size="icon-sm"
      className="relative"
      render={
        <Link
          to="/notifications"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications"}
        />
      }
    >
      <Bell className="size-5" />
      <UnreadBadge className="top-0.5 right-0.5" />
    </Button>
  );
}

interface TabItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end: boolean;
  prominent?: boolean;
  withBadge?: boolean;
}

const TAB_ITEMS: TabItem[] = [
  { to: "/", label: "Home", icon: House, end: true },
  { to: "/requests", label: "Requests", icon: ReceiptText, end: true },
  { to: "/requests/new", label: "New", icon: SquarePlus, end: false, prominent: true },
  { to: "/notifications", label: "Alerts", icon: Bell, end: false, withBadge: true },
];

function BottomNav() {
  return (
    <nav className="pb-safe border-t border-border bg-card/95 backdrop-blur" aria-label="Primary">
      <ul className="grid grid-cols-4">
        {TAB_ITEMS.map(({ to, label, icon: Icon, end, prominent, withBadge }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "relative flex items-center justify-center transition-colors",
                      prominent
                        ? "-mt-4 size-11 rounded-full bg-primary text-primary-foreground shadow-md"
                        : cn("h-7 w-12 rounded-full", isActive && "bg-accent")
                    )}
                  >
                    <Icon
                      className={cn(prominent ? "size-6" : "size-5")}
                      strokeWidth={isActive ? 2.4 : 1.8}
                    />
                    {withBadge && <UnreadBadge className="-top-0.5 right-1" />}
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
